/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { hh } from "@chi/chi";
import { type ChiElement, type ChiItem, type ChiNode, ChiRuntime } from "@chi/chi/hooks";
import type { WbaxParam, WbaxRef } from "@/types.tsx";
import { ApplyAutomation } from "@/automation.tsx";
import { WbaxCommonAttrs, WbaxNodeAcceptsInput, type WbaxNodeDef, WbaxNodes } from "@/nodes.tsx";
import { ScopeValue, type WbaxEnv, WbaxEnvContext, WbaxTimeContext } from "@/context.tsx";

interface Instance {
  tag: string;
  path: string;
  gen: number;
  def: WbaxNodeDef;
  node: AudioNode;
  props: Record<string, any>;
  base: number;
  subs: Map<string, rxjs.Subscription>;
  started: boolean;
  ended: boolean;
}

interface Edge {
  from: AudioNode;
  to: AudioNode | AudioParam;
  output: number;
  input: number;
  param: boolean;
}

interface Port {
  inst: Instance;
  outlet: number;
}

interface Built {
  input: Instance | null;
  outputs: Port[];
}

type Target = { inst: Instance; inlet: number } | { inst: Instance; param: string } | null;

const StructuralTags = new Set(["chain", "mod", "use", "defs"]);

const IsElement = (item: ChiItem): item is ChiElement => typeof item !== "string";

const IsPlain = (v: unknown): v is object =>
  v !== null && typeof v === "object" &&
  (Array.isArray(v) || Object.getPrototypeOf(v) === Object.prototype);

const IsObservable = (v: unknown): v is rxjs.Observable<number> =>
  typeof (v as { subscribe?: unknown } | null)?.subscribe === "function";

export function WbaxSame(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (!IsPlain(a) || !IsPlain(b)) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (_e) {
    return false;
  }
}

function SetRef<N>(ref: WbaxRef<N> | undefined, node: N | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(node);
  else ref.current = node;
}

function Quietly(work: () => void): boolean {
  try {
    work();
    return true;
  } catch (_e) {
    return false;
  }
}

function Connect(edge: Edge) {
  if (edge.param) edge.from.connect(edge.to as AudioParam, edge.output);
  else edge.from.connect(edge.to as AudioNode, edge.output, edge.input);
}

function Disconnect(edge: Edge) {
  Quietly(() => {
    if (edge.param) edge.from.disconnect(edge.to as AudioParam, edge.output);
    else edge.from.disconnect(edge.to as AudioNode, edge.output, edge.input);
  });
}

export interface WbaxRootOptions {
  ctx: BaseAudioContext;
  destination?: AudioNode;
  time?: number;
  runtime?: ChiRuntime;
  smoothing?: number;
  create?: WbaxCreate;
}

export interface WbaxCreateInfo {
  tag: string;
  path: string;
}

export type WbaxCreate = (make: () => AudioNode, info: WbaxCreateInfo) => AudioNode;

export class WbaxRoot {
  readonly ctx: BaseAudioContext;
  readonly destination: AudioNode;
  readonly runtime: ChiRuntime;
  readonly time: number;
  readonly rendered$ = new rxjs.Subject<void>();
  readonly error$ = new rxjs.Subject<unknown>();

  #node: ChiNode = null;
  #instances = new Map<string, Instance>();
  #ids = new Map<string, Instance>();
  #edges = new Map<string, Edge>();
  #gen = 0;
  #scheduled = false;
  #disposed = false;
  #ownsRuntime: boolean;
  #smoothing: number;
  #create: WbaxCreate;
  #sub: rxjs.Subscription;
  #env: WbaxEnv;

  constructor(options: WbaxRootOptions) {
    this.ctx = options.ctx;
    this.destination = options.destination ?? options.ctx.destination;
    this.time = options.time ?? options.ctx.currentTime;
    this.runtime = options.runtime ?? new ChiRuntime();
    this.#ownsRuntime = !options.runtime;
    this.#smoothing = options.smoothing ?? 0.01;
    this.#create = options.create ?? ((make) => make());
    this.#sub = this.runtime.invalidate$.subscribe(() => this.#schedule());
    this.#env = {
      ctx: this.ctx,
      destination: this.destination,
      getNode: <N extends AudioNode>(id: string) => this.#ids.get(id)?.node as N | undefined,
    };
  }

  get disposed(): boolean {
    return this.#disposed;
  }

  get nodes(): ReadonlyMap<string, AudioNode> {
    return new Map([...this.#instances].map(([path, inst]) => [path, inst.node]));
  }

  get edgeCount(): number {
    return this.#edges.size;
  }

  getNode<N extends AudioNode = AudioNode>(id: string): N | undefined {
    return this.#ids.get(id)?.node as N | undefined;
  }

  render(node: ChiNode) {
    if (this.#disposed) throw new Error("wbax: root is disposed");
    this.#node = node;
    this.#render();
  }

  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#sub.unsubscribe();
    for (const edge of this.#edges.values()) Disconnect(edge);
    this.#edges.clear();
    for (const inst of [...this.#instances.values()]) this.#destroy(inst);
    this.#ids.clear();
    if (this.#ownsRuntime) this.runtime.dispose();
    this.rendered$.complete();
    this.error$.complete();
  }

  #schedule() {
    if (this.#scheduled || this.#disposed) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      if (this.#disposed) return;
      try {
        this.#render();
      } catch (error) {
        if (this.error$.observed) this.error$.next(error);
        else {setTimeout(() => {
            throw error;
          });}
      }
    });
  }

  #render() {
    const tree = hh(
      WbaxEnvContext.Provider,
      { value: this.#env },
      hh(WbaxTimeContext.Provider, { value: this.time }, this.#node),
    );

    this.runtime.pass(() => {
      const items = this.runtime.expand(tree);
      const seen = new Set<string>();
      this.#ids.clear();
      this.#mount(items, seen);
      this.#wire(items);
      for (const [path, inst] of [...this.#instances]) {
        if (!seen.has(path)) this.#destroy(inst);
      }
    });
    this.rendered$.next();
  }

  //#region mount
  #mount(items: ChiItem[], seen: Set<string>) {
    for (const item of items) {
      if (!IsElement(item)) continue;
      if (!StructuralTags.has(item.tag)) {
        const def = WbaxNodes[item.tag];
        if (!def) throw new Error(`wbax: unknown element <${item.tag}>`);
        const inst = this.#upsert(item, def);
        seen.add(item.path);
        const id = item.props.id as string | undefined;
        if (id !== undefined) {
          if (this.#ids.has(id)) throw new Error(`wbax: duplicate id "${id}"`);
          this.#ids.set(id, inst);
        }
      }
      this.#mount(item.children, seen);
    }
  }

  #upsert(el: ChiElement, def: WbaxNodeDef): Instance {
    const props = el.props;
    const base = ScopeValue(el.scope, WbaxTimeContext);
    let inst = this.#instances.get(el.path);

    if (
      inst && (
        inst.tag !== el.tag ||
        (def.source && inst.base !== base) ||
        (def.recreate ?? []).some((k) => !WbaxSame(inst!.props[k], props[k]))
      )
    ) {
      this.#destroy(inst);
      inst = undefined;
    }

    if (!inst) {
      const node = this.#create(() => def.create(this.ctx, props), { tag: el.tag, path: el.path });
      const created: Instance = {
        tag: el.tag,
        path: el.path,
        gen: ++this.#gen,
        def,
        node,
        props: {},
        base,
        subs: new Map(),
        started: false,
        ended: false,
      };
      this.#instances.set(el.path, created);
      this.#apply(created, props, true, false);
      if (def.source) this.#start(created, props);
      created.props = props;
      SetRef(props.ref, node);
      return created;
    }

    const rebase = inst.base !== base;
    inst.base = base;
    this.#apply(inst, props, false, rebase);
    if (
      def.source && !inst.ended && props.stop !== undefined &&
      !WbaxSame(inst.props.stop, props.stop)
    ) {
      Quietly(() => (inst!.node as AudioScheduledSourceNode).stop(inst!.base + props.stop));
    }
    if (inst.props.ref !== props.ref) {
      SetRef(inst.props.ref, null);
      SetRef(props.ref, inst.node);
    }
    inst.props = props;
    return inst;
  }

  #start(inst: Instance, props: Record<string, any>) {
    const node = inst.node as AudioScheduledSourceNode;
    node.addEventListener("ended", () => {
      inst.ended = true;
      inst.props.onEnded?.();
    }, { once: true });
    inst.def.source!(node, Math.max(0, inst.base + (props.start ?? 0)), props);
    inst.started = true;
    if (props.stop !== undefined) node.stop(Math.max(0, inst.base + props.stop));
  }

  #apply(inst: Instance, props: Record<string, any>, fresh: boolean, rebase: boolean) {
    const { def, node } = inst;
    const prev = fresh ? {} : inst.props;

    for (const name of def.params ?? []) {
      this.#param(inst, name, (node as any)[name], prev[name], props[name], fresh, rebase);
    }

    const dyn = def.dynamicParams?.(props) ?? {};
    const prevDyn = fresh ? {} : def.dynamicParams?.(prev) ?? {};
    for (const name of new Set([...Object.keys(dyn), ...Object.keys(prevDyn)])) {
      const param = def.paramOf?.(node, name);
      if (param) {
        this.#param(inst, `params.${name}`, param, prevDyn[name], dyn[name], fresh, rebase);
      }
    }

    const attrs: [string, true | ((n: any, v: any, c: BaseAudioContext) => void)][] = [
      ...WbaxCommonAttrs.map((name) => [name, true] as [string, true]),
      ...Object.entries(def.attrs ?? {}),
    ];
    for (const [name, set] of attrs) {
      const value = props[name];
      if (value === undefined || (!fresh && WbaxSame(prev[name], value))) continue;
      if (set === true) (node as any)[name] = value;
      else set(node, value, this.ctx);
    }
  }

  #param(
    inst: Instance,
    key: string,
    param: AudioParam,
    prev: WbaxParam | undefined,
    next: WbaxParam | undefined,
    fresh: boolean,
    rebase: boolean,
  ) {
    if (!fresh && WbaxSame(prev, next) && !(rebase && Array.isArray(next))) return;

    inst.subs.get(key)?.unsubscribe();
    inst.subs.delete(key);
    if (next === undefined) return;

    if (typeof next === "number") {
      if (fresh) param.value = next;
      else param.setTargetAtTime(next, this.ctx.currentTime, this.#smoothing);
    } else if (Array.isArray(next)) {
      if (!fresh) param.cancelScheduledValues(this.ctx.currentTime);
      ApplyAutomation(param, next, inst.base);
    } else if (IsObservable(next)) {
      let sync = fresh;
      inst.subs.set(
        key,
        next.subscribe((value) => {
          if (sync) param.value = value;
          else param.setTargetAtTime(value, this.ctx.currentTime, this.#smoothing);
        }),
      );
      sync = false;
    }
  }

  #destroy(inst: Instance) {
    if (this.#instances.get(inst.path) === inst) this.#instances.delete(inst.path);
    for (const sub of inst.subs.values()) sub.unsubscribe();
    inst.subs.clear();
    SetRef(inst.props.ref, null);
    if (inst.def.external) return;
    if (inst.def.source && inst.started && !inst.ended) {
      Quietly(() => (inst.node as AudioScheduledSourceNode).stop());
    }
    Quietly(() => inst.node.disconnect());
  }
  //#endregion

  //#region wire
  #wire(items: ChiItem[]) {
    const next = new Map<string, Edge>();
    for (const item of items) {
      for (const out of this.#build(item, next).outputs) this.#link(next, out, null);
    }

    for (const [key, edge] of this.#edges) if (!next.has(key)) Disconnect(edge);
    for (const [key, edge] of next) if (!this.#edges.has(key)) Connect(edge);
    this.#edges = next;
  }

  #build(item: ChiItem, edges: Map<string, Edge>): Built {
    if (!IsElement(item)) return { input: null, outputs: [] };

    if (item.tag === "defs") {
      for (const child of item.children) this.#build(child, edges);
      return { input: null, outputs: [] };
    }

    if (item.tag === "chain") {
      const steps = item.children.filter(IsElement).filter((step) => {
        if (step.tag !== "defs") return true;
        this.#build(step, edges);
        return false;
      });
      const built = steps.map((step) => this.#build(step, edges));
      for (let i = 1; i < built.length; i++) {
        const input = built[i].input;
        if (!input && built[i - 1].outputs.length) {
          throw new Error(`wbax: <${steps[i].tag}> in a <chain> has no audio input`);
        }
        for (const out of built[i - 1].outputs) {
          this.#link(edges, out, { inst: input!, inlet: steps[i - 1].props.inlet ?? 0 });
        }
      }
      return { input: built[0]?.input ?? null, outputs: built.at(-1)?.outputs ?? [] };
    }

    if (item.tag === "use") {
      const inst = this.#ids.get(item.props.id);
      if (!inst) throw new Error(`wbax: <use> references unknown id "${item.props.id}"`);
      return { input: null, outputs: [{ inst, outlet: item.props.outlet ?? 0 }] };
    }

    if (item.tag === "mod") {
      throw new Error("wbax: <mod> must be a direct child of a node element");
    }

    const inst = this.#instances.get(item.path)!;
    const accepts = WbaxNodeAcceptsInput(inst.def, inst.node);

    for (const child of item.children) {
      if (!IsElement(child)) continue;

      if (child.tag === "mod") {
        const name = child.props.param as string;
        for (const grand of child.children) {
          for (const out of this.#build(grand, edges).outputs) {
            this.#link(edges, out, { inst, param: name });
          }
        }
        continue;
      }

      const built = this.#build(child, edges);
      if (!built.outputs.length) continue;
      if (!accepts) throw new Error(`wbax: <${item.tag}> has no audio input`);
      for (const out of built.outputs) {
        this.#link(edges, out, { inst, inlet: child.props.inlet ?? 0 });
      }
    }

    return {
      input: accepts ? inst : null,
      outputs: [{ inst, outlet: item.props.outlet ?? 0 }],
    };
  }

  #link(edges: Map<string, Edge>, from: Port, to: Target) {
    const src = `${from.inst.path}@${from.inst.gen}:${from.outlet}`;

    if (to === null) {
      edges.set(`${src}->#destination`, {
        from: from.inst.node,
        to: this.destination,
        output: from.outlet,
        input: 0,
        param: false,
      });
      return;
    }

    const dst = `${to.inst.path}@${to.inst.gen}`;
    if ("param" in to) {
      const param = this.#paramOf(to.inst, to.param);
      edges.set(`${src}->${dst}.${to.param}`, {
        from: from.inst.node,
        to: param,
        output: from.outlet,
        input: 0,
        param: true,
      });
      return;
    }

    edges.set(`${src}->${dst}:${to.inlet}`, {
      from: from.inst.node,
      to: to.inst.node,
      output: from.outlet,
      input: to.inlet,
      param: false,
    });
  }

  #paramOf(inst: Instance, name: string): AudioParam {
    const param = (inst.def.params ?? []).includes(name)
      ? (inst.node as any)[name] as AudioParam
      : inst.def.paramOf?.(inst.node, name);
    if (!param) throw new Error(`wbax: <${inst.tag}> has no param "${name}"`);
    return param;
  }
  //#endregion
}

export interface WbaxOfflineOptions {
  duration: number;
  sampleRate?: number;
  channels?: number;
}

export async function WbaxRenderOffline(
  node: ChiNode,
  options: WbaxOfflineOptions,
): Promise<AudioBuffer> {
  const sampleRate = options.sampleRate ?? 44100;
  const ctx = new OfflineAudioContext(
    options.channels ?? 1,
    Math.max(1, Math.ceil(options.duration * sampleRate)),
    sampleRate,
  );
  const root = new WbaxRoot({ ctx, time: 0 });
  try {
    root.render(node);
    return await ctx.startRendering();
  } finally {
    root.dispose();
  }
}
